import type {
  BoardSummary,
  CreateTaskBody,
  TaskColor,
  TaskStage,
  TaskSummary,
} from '@obras/shared';
import { TASK_URGENCIES, TASK_URGENCY_LABELS } from '@obras/shared';
import { Check, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '../../components/ui/Button.js';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { Modal } from '../../components/ui/Modal.js';
import { ApiError } from '../../lib/api.js';
import { useOrgMembers } from '../usuarios/api.js';
import { ColorPicker } from './ColorPicker.js';
import { PeoplePicker, type Person } from './PeoplePicker.js';
import {
  useAddParticipant,
  useCreateTask,
  useDeleteTask,
  useRemoveParticipant,
  useUpdateTask,
} from './api.js';

/**
 * Checklist é texto: uma linha, um item.
 *
 * A conversão entre a lista e o texto acontece aqui, e só aqui. Marcar item
 * não passa por este campo — isso é da tela de leitura, com caixa de seleção,
 * porque marcar é a ação corriqueira e não deveria exigir "salvar".
 */
const checklistToText = (task: TaskSummary | undefined): string =>
  (task?.checklist ?? []).map((item) => item.content).join('\n');

interface FormState {
  title: string;
  description: string;
  dueAt: string;
  placement: string;
  color: TaskColor | null;
  urgency: string;
  participantIds: string[];
  checklist: string;
}

function initialState(
  task: TaskSummary | undefined,
  board: BoardSummary,
  initialPlacement: string | undefined,
): FormState {
  return {
    title: task?.title ?? '',
    description: task?.description ?? '',
    dueAt: task?.dueAt ?? '',
    placement:
      task?.columnId ?? task?.stage ?? initialPlacement ?? board.columns[0]?.id ?? 'a_fazer',
    color: task?.color ?? null,
    urgency: task?.urgency ?? 'normal',
    participantIds: (task?.participants ?? []).map((person) => person.id),
    checklist: checklistToText(task),
  };
}

export function TaskFormModal({
  open,
  onClose,
  board,
  task,
  initialPlacement,
}: {
  open: boolean;
  onClose: () => void;
  board: BoardSummary;
  task?: TaskSummary | undefined;
  /** Coluna ou etapa de onde o "adicionar" partiu. */
  initialPlacement?: string | undefined;
}) {
  const [form, setForm] = useState<FormState>(() => initialState(task, board, initialPlacement));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const create = useCreateTask(board.id);
  const update = useUpdateTask(board.id);
  const remove = useDeleteTask(board.id);
  const addParticipant = useAddParticipant(board.id);
  const removeParticipant = useRemoveParticipant(board.id);
  const members = useOrgMembers();

  const saving = create.isPending || update.isPending;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((current) => ({ ...current, [key]: value }));

  const people: Person[] = (members.data?.items ?? []).map((member) => ({
    id: member.id,
    name: member.name,
    detail: member.jobTitle ?? member.email ?? undefined,
  }));

  /*
   * Numa tarefa que já existe, incluir e remover valem na hora — são atos
   * sobre pessoas, não rascunho de texto. Na criação a lista viaja junto com o
   * resto do formulário, porque ainda não há tarefa a que se referir.
   */
  function togglePerson(id: string, next: boolean): void {
    set(
      'participantIds',
      next ? [...form.participantIds, id] : form.participantIds.filter((current) => current !== id),
    );

    if (!task) return;

    const action = next
      ? addParticipant.mutateAsync({ taskId: task.id, userId: id })
      : removeParticipant.mutateAsync({ taskId: task.id, userId: id });

    void action.catch((error: unknown) => {
      setFormError(error instanceof ApiError ? error.message : 'Não foi possível alterar.');
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    const placement = board.isPersonal
      ? { stage: form.placement as TaskStage }
      : { boardId: board.id, columnId: form.placement };

    try {
      if (task) {
        await update.mutateAsync({
          id: task.id,
          title: form.title.trim(),
          description: form.description.trim() || undefined,
          dueAt: form.dueAt || undefined,
          color: form.color,
          urgency: form.urgency as CreateTaskBody['urgency'],
          checklist: form.checklist,
          ...(board.isPersonal ? { stage: placement.stage } : { columnId: placement.columnId }),
        });
      } else {
        const body: CreateTaskBody = {
          title: form.title.trim(),
          description: form.description.trim() || undefined,
          dueAt: form.dueAt || undefined,
          ...(form.color ? { color: form.color } : {}),
          urgency: form.urgency as CreateTaskBody['urgency'],
          participantIds: form.participantIds,
          checklist: form.checklist,
          ...placement,
        };
        await create.mutateAsync(body);
      }

      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors());
        setFormError(error.details ? 'Verifique os campos destacados.' : error.message);
      } else {
        setFormError('Não foi possível salvar. Tente novamente.');
      }
    }
  }

  async function handleDelete(): Promise<void> {
    if (!task) return;

    try {
      await remove.mutateAsync(task.id);
      onClose();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : 'Não foi possível excluir.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={task ? 'Editar tarefa' : 'Nova tarefa'}
      description={board.isPersonal ? 'Quadro Pessoal' : board.name}
      footer={
        <>
          {task?.canManage ? (
            <Button
              variant="ghost"
              className="mr-auto text-danger"
              icon={<Trash2 size={16} strokeWidth={1.5} />}
              loading={remove.isPending}
              onClick={() => void handleDelete()}
            >
              Excluir
            </Button>
          ) : null}

          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            form="task-form"
            type="submit"
            loading={saving}
            icon={<Check size={16} strokeWidth={1.5} />}
          >
            {task ? 'Salvar' : 'Criar tarefa'}
          </Button>
        </>
      }
    >
      <form
        id="task-form"
        onSubmit={(event) => void handleSubmit(event)}
        className="flex flex-col gap-5"
        noValidate
      >
        {formError ? (
          <div role="alert" className="rounded-md bg-danger-bg px-3.5 py-3 text-[13px] text-danger">
            {formError}
          </div>
        ) : null}

        <TextField
          label="Título"
          required
          value={form.title}
          onChange={(event) => set('title', event.target.value)}
          {...(fieldErrors['title'] ? { error: fieldErrors['title'] } : {})}
        />

        <TextAreaField
          label="Descrição"
          rows={3}
          value={form.description}
          onChange={(event) => set('description', event.target.value)}
          {...(fieldErrors['description'] ? { error: fieldErrors['description'] } : {})}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            label={board.isPersonal ? 'Etapa' : 'Coluna'}
            value={form.placement}
            onChange={(event) => set('placement', event.target.value)}
          >
            {board.columns.map((column) => (
              <option key={column.id} value={column.id}>
                {column.name}
              </option>
            ))}
          </SelectField>

          <SelectField
            label="Urgência"
            value={form.urgency}
            onChange={(event) => set('urgency', event.target.value)}
          >
            {TASK_URGENCIES.map((urgency) => (
              <option key={urgency} value={urgency}>
                {TASK_URGENCY_LABELS[urgency]}
              </option>
            ))}
          </SelectField>

          <TextField
            label="Prazo"
            type="date"
            mono
            value={form.dueAt}
            onChange={(event) => set('dueAt', event.target.value)}
            {...(fieldErrors['dueAt'] ? { error: fieldErrors['dueAt'] } : {})}
          />
        </div>

        <ColorPicker value={form.color} onChange={(color) => set('color', color)} />

        <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">Participantes</span>
          <PeoplePicker
            people={people}
            selected={form.participantIds}
            onToggle={togglePerson}
            lockedIds={task ? [task.createdBy.id] : []}
            emptyLabel={members.isLoading ? 'Carregando…' : 'Ninguém encontrado.'}
          />
          <p className="text-[13px] text-muted-foreground">
            A tarefa aparece no quadro Pessoal de cada pessoa marcada.
          </p>
        </div>

        <TextAreaField
          label="Checklist"
          rows={4}
          placeholder={'Conferir ART\nSolicitar cronograma'}
          hint="Cada linha vira um item. O que já estava marcado continua marcado."
          value={form.checklist}
          onChange={(event) => set('checklist', event.target.value)}
        />
      </form>
    </Modal>
  );
}
